import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Package, ShoppingCart, FileBarChart2, LogOut, Tv, Plus, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/sales", label: "Sales", icon: ShoppingCart, testid: "nav-sales" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testid: "nav-reports" },
  { to: "/help", label: "Help", icon: Sparkles, testid: "nav-help" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-muted/40">
      {/* Sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 bg-navy text-white flex-col z-40">
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-logo-link">
            <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center">
              <Tv className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="font-heading font-semibold text-lg leading-none">Top Media</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/60 mt-1">Entertainment Corp.</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `nav-item ${isActive ? "nav-item-active" : ""}`
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-white/50">
          v1 · Internal Tool
        </div>
      </aside>

      {/* Topbar + Main */}
      <div className="md:ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-border/60 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-2 md:hidden">
            <Tv className="w-5 h-5 text-navy" />
            <span className="font-heading font-semibold">Top Media</span>
          </div>
          <div className="hidden md:block">
            <h2 className="font-heading text-lg font-medium text-navy">Welcome back, {user?.name}</h2>
            <p className="text-xs text-muted-foreground">Role: {user?.role === "admin" ? "Administrator" : "Staff"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/sales/new">
              <Button data-testid="topbar-add-sale-btn" className="bg-gold hover:bg-gold-hover text-white">
                <Plus className="w-4 h-4 mr-1" /> Add Sale
              </Button>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-navy text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-menu-item" onClick={onLogout} className="text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-navy text-white flex items-center justify-around py-2 z-40 border-t border-white/10">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`mobile-${item.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded ${isActive ? "text-gold" : "text-white/70"}`
              }>
              <item.icon className="w-4 h-4" />
              <span className="text-[10px]">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="md:hidden h-16" />
      </div>
    </div>
  );
}
